import { makeStyles, tokens, Text } from '@fluentui/react-components';
import { formatUsd, fmtMonth } from '../lib/staffUsageFormat.js';

/**
 * Gráfico de barras simple para costos mensuales. CSS puro — sin librería.
 *
 * Cada bucket representa 1 mes. Si no hay buckets, muestra mensaje vacío.
 */
export interface MonthlyBucket {
  month: string;
  costUsd: number;
  turns: number;
}

interface Props {
  data: MonthlyBucket[];
  height?: number;
}

const useStyles = makeStyles({
  empty: { color: tokens.colorNeutralForeground3, padding: '12px 0' },
  chart: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '8px',
    height: 'var(--chart-h)',
    paddingTop: '16px',
    paddingBottom: '4px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  bar: {
    flex: 1,
    minWidth: '12px',
    background: tokens.colorBrandBackground,
    borderRadius: `${tokens.borderRadiusSmall} ${tokens.borderRadiusSmall} 0 0`,
    position: 'relative',
    transition: 'background 120ms',
    ':hover': { background: tokens.colorBrandBackgroundHover },
  },
  barEmpty: {
    flex: 1,
    minWidth: '12px',
    background: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusSmall,
    height: '4px',
    alignSelf: 'flex-end',
  },
  labelsRow: {
    display: 'flex',
    gap: '8px',
    marginTop: '4px',
  },
  monthLabel: {
    flex: 1,
    minWidth: '12px',
    textAlign: 'center',
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
});

export function MonthlyBarChart({ data, height = 180 }: Props) {
  const styles = useStyles();
  if (data.length === 0) {
    return <div className={styles.empty}>Sin actividad en este período.</div>;
  }
  const max = Math.max(...data.map((d) => d.costUsd), 0.0001);
  return (
    <div style={{ '--chart-h': `${height}px` } as React.CSSProperties}>
      <div className={styles.chart}>
        {data.map((d) => {
          const pct = (d.costUsd / max) * 100;
          if (d.costUsd <= 0) {
            return <div key={d.month} className={styles.barEmpty} title={`${fmtMonth(d.month)}: sin actividad`} />;
          }
          return (
            <div
              key={d.month}
              className={styles.bar}
              style={{ height: `${Math.max(pct, 1.5)}%` }}
              title={`${fmtMonth(d.month)} — ${formatUsd(d.costUsd)} · ${d.turns} turnos`}
            />
          );
        })}
      </div>
      <div className={styles.labelsRow}>
        {data.map((d) => (
          <span key={d.month} className={styles.monthLabel}>{fmtMonth(d.month)}</span>
        ))}
      </div>
    </div>
  );
}
